import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';

export interface ExpandableCardProps {
  title: string;
  /** DEPRECATED: Ícones não são mais exibidos. Mantido para retrocompatibilidade. */
  icon?: React.ReactNode;
  badge?: number | string;
  badgeColor?: string;
  headerAction?: React.ReactNode;
  /** Estado de expansão (controlado externamente) */
  expanded?: boolean;
  /** Estado inicial se não controlado */
  defaultExpanded?: boolean;
  onToggle?: (expanded: boolean) => void;
  children?: React.ReactNode;
  style?: React.CSSProperties;
  noPadding?: boolean;
  variant?: 'default' | 'fieldset';
}

/**
 * ExpandableCard - Card expansível com header clicável
 *
 * Suporta dois estilos visuais:
 * - 'default': Header com background cinza, chevron à esquerda
 * - 'fieldset': Título na borda superior (estilo fieldset/legend), visual limpo
 */
export default function ExpandableCard({
  title,
  // NOTE: prop `icon` is DEPRECATED (mantido para retrocompatibilidade via ExpandableCardProps)
  // e intencionalmente não desestruturada — ícones não são mais renderizados.
  badge,
  badgeColor = '#374151',
  headerAction,
  expanded: controlledExpanded,
  defaultExpanded = false,
  onToggle,
  children,
  style,
  noPadding = false,
  variant = 'default'
}: ExpandableCardProps) {
  // Estado interno para quando não é controlado
  const [internalExpanded, setInternalExpanded] = useState<boolean>(defaultExpanded);

  // Usa estado controlado se fornecido, senão usa interno
  const isExpanded = controlledExpanded !== undefined ? controlledExpanded : internalExpanded;

  const handleToggle = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();

    if (controlledExpanded !== undefined) {
      // Modo controlado: chama callback
      if (onToggle) onToggle(!isExpanded);
    } else {
      // Modo não controlado: atualiza estado interno
      setInternalExpanded(!isExpanded);
      if (onToggle) onToggle(!isExpanded);
    }
  };

  // Estilos do card (padrão do Design System - GenericDrawer)
  const cardStyle = {
    background: 'white',
    border: '1px solid var(--panel-border)',
    borderRadius: '8px', // Cantos ligeiramente arredondados
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    marginBottom: '12px', // Espaçamento padrão entre cards (igual V1)
    ...style
  };

  const headerStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    cursor: 'pointer',
    background: '#f0f0f0',
    borderBottom: isExpanded ? '1px solid var(--panel-border)' : 'none',
    transition: 'background-color 0.15s ease'
  };

  const titleContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flex: 1
  };

  const titleStyle = {
    fontSize: '14px',
    fontWeight: 600,
    color: '#374151',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  };

  const badgeStyle = {
    background: 'var(--panel-border)', // Cinza mais escuro
    borderRadius: '12px',
    padding: '2px 8px',
    fontSize: '12px',
    color: badgeColor,
    fontWeight: 500
  };

  const chevronStyle = {
    color: 'var(--muted)',
    transition: 'transform 0.2s ease',
    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
    flexShrink: 0
  };

  const contentStyle = {
    padding: noPadding ? 0 : '16px',
    display: isExpanded ? 'block' : 'none'
  };

  // Estilo padrão para botões no headerAction (mesmo padrão do btn-add do GenericHeader)
  const headerActionButtonStyle = {
    backgroundColor: 'var(--color-primary)',
    color: 'white',
    border: '1px solid var(--color-primary)',
    padding: '4px 10px',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '12px',
    fontWeight: 500,
    boxShadow: 'var(--shadow-button-primary)',
    transition: 'all 0.2s ease'
  };

  // Aplica estilos padrão aos botões do headerAction
  const renderHeaderAction = () => {
    if (!headerAction) return null;

    // Se headerAction for um elemento React do tipo button, aplica estilos padrão
    if (
      React.isValidElement<React.ButtonHTMLAttributes<HTMLButtonElement>>(headerAction) &&
      headerAction.type === 'button'
    ) {
      return React.cloneElement(headerAction, {
        style: { ...headerActionButtonStyle, ...headerAction.props.style }
      });
    }

    return headerAction;
  };

  // === Variante Fieldset ===
  if (variant === 'fieldset') {
    return (
      <div style={{
        position: 'relative',
        border: '1px solid var(--panel-border)',
        borderRadius: '8px',
        marginTop: '16px', // Espaço para o título que fica em top: -10px
        marginBottom: '12px',
        background: 'white',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        ...style
      }}>
        {/* Header clicável no estilo fieldset/legend */}
        <div
          onClick={handleToggle}
          style={{
            position: 'absolute',
            top: '-10px',
            left: '12px',
            right: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            zIndex: 1
          }}
        >
          {/* Lado esquerdo: chevron, título e badge */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            background: 'white',
            padding: '0 8px'
          }}>
            <ChevronDown
              size={14}
              style={{
                color: 'var(--muted)',
                transition: 'transform 0.2s ease',
                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
              }}
            />
            <span style={{
              fontSize: '12px',
              fontWeight: 600,
              color: 'var(--muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              {title}
            </span>
            {(badge !== undefined && badge !== null) && (
              <span style={{
                background: 'var(--panel-border)',
                borderRadius: '10px',
                padding: '1px 6px',
                fontSize: '11px',
                color: badgeColor,
                fontWeight: 500
              }}>
                {badge}
              </span>
            )}
          </div>

          {/* Lado direito: ação do header */}
          {headerAction && (
            <div
              onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
              style={{ background: 'white', padding: '0 4px' }}
            >
              {renderHeaderAction()}
            </div>
          )}
        </div>

        {/* Conteúdo */}
        <div style={{
          padding: noPadding ? '16px 0 0' : '20px 16px 16px',
          display: isExpanded ? 'block' : 'none'
        }}>
          {children}
        </div>

        {/* Quando recolhido, mostrar indicador mínimo */}
        {!isExpanded && (
          <div style={{ height: '8px' }} />
        )}
      </div>
    );
  }

  // === Variante Default (original) ===
  return (
    <div style={cardStyle}>
      {/* Header clicável */}
      <div
        style={headerStyle}
        onClick={handleToggle}
        onMouseEnter={(e: React.MouseEvent<HTMLDivElement>) => { e.currentTarget.style.background = '#e8e8e8'; }}
        onMouseLeave={(e: React.MouseEvent<HTMLDivElement>) => { e.currentTarget.style.background = '#f0f0f0'; }}
      >
        {/* Lado esquerdo: chevron, título e badge */}
        <div style={titleContainerStyle}>
          {/* Chevron no lado ESQUERDO do label */}
          <ChevronDown size={18} style={chevronStyle} />
          <span style={titleStyle}>{title}</span>
          {(badge !== undefined && badge !== null) && (
            <span style={badgeStyle}>{badge}</span>
          )}
        </div>

        {/* Lado direito: apenas ação do header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Ação do header (para ao clicar no botão) */}
          {headerAction && (
            <div onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}>
              {renderHeaderAction()}
            </div>
          )}
        </div>
      </div>

      {/* Conteúdo */}
      <div style={contentStyle}>
        {children}
      </div>
    </div>
  );
}

/**
 * Exemplo de uso:
 *
 * const [expanded, setExpanded] = useState(true);
 *
 * // Variante default (header cinza)
 * <ExpandableCard
 *   title="Recebimentos"
 *   badge={5}
 *   expanded={expanded}
 *   onToggle={setExpanded}
 *   headerAction={
 *     <button onClick={handleAdd}>
 *       <Plus size={14} /> Adicionar
 *     </button>
 *   }
 * >
 *   <MovimentosList movimentos={movimentos} />
 * </ExpandableCard>
 *
 * // Variante fieldset (título na borda - mesmo estilo do FieldsetCard)
 * <ExpandableCard
 *   variant="fieldset"
 *   title="Endereços"
 *   badge={2}
 *   expanded={expanded}
 *   onToggle={setExpanded}
 * >
 *   <EnderecosList enderecos={enderecos} />
 * </ExpandableCard>
 *
 * Variantes disponíveis:
 * - 'default': Header com background cinza, chevron à esquerda
 * - 'fieldset': Título na borda superior (estilo fieldset/legend)
 *
 * Padrão visual (Design System):
 * - Chevron de expansão à ESQUERDA do título
 * - SEM ícones decorativos (prop icon é ignorada)
 * - Badge com fundo cinza escuro (#e5e7eb)
 * - Espaçamento compacto entre cards (12px)
 * - Cantos arredondados (8px)
 * - Botões no headerAction recebem estilo padrão automaticamente
 */
